/**
 * Story 4.7 (AC 4) — the staleness strings, as a LEAF module.
 *
 * WHY THIS EXISTS. The epic asks for i18n keys "shared with the v3 module, not
 * duplicated, so wording can never drift" between the simulation screen and the
 * legacy edition screen. That was not achievable while the strings lived only
 * inside `translations.ts`: `registerSimulationV3Translations()` is called at
 * module scope in `SimulationScreen.tsx`, and the only import of that screen is
 * `React.lazy(() => import('./index'))` in `routes.tsx`. A dynamic import's
 * factory runs when the chunk is fetched AND executed — i.e. when React renders
 * the lazy element. A planner sitting on `components/newMasterPlanogram/prePlan/`
 * has rendered neither render site, so i18next holds no `simulationV3.*` keys
 * there and every `t('simulationV3.staleness.…')` renders its raw key string
 * (the app installs no `parseMissingKeyHandler`).
 *
 * A CORRECTION WORTH KEEPING. The implementation-readiness report gives the
 * reason for this as "the flag is off by default". That is false —
 * `REACT_APP_SIMULATION_V3=true` in `.env.development`, `.env.qa` AND
 * `.env.prod`. The conclusion holds for a sturdier reason: the chunk boundary,
 * which no flag value changes.
 *
 * THE RULE FOR THIS FILE. It must import `i18next` and nothing else — no React,
 * no store, no CSS, no module internals — so that importing it from the legacy
 * screen pulls in no part of the v3 chunk (NFR-9: flag off => zero change). A
 * source-scan test in `__tests__/StalenessBundle.test.ts` enforces that.
 *
 * `translations.ts` splices this subtree back into `SIMULATION_V3_TRANSLATIONS`,
 * so the v3 screen is unchanged and `Rtl.test.tsx`'s en/he parity assertion
 * keeps covering these strings. Do NOT "simplify" that by removing the splice:
 * the parity test would still pass while silently ceasing to cover them.
 */
import i18n from 'i18next'

/** The subset of i18next this module needs — injectable for tests. */
interface ResourceHost {
  addResourceBundle?: (
    language: string,
    namespace: string,
    resources: Record<string, unknown>,
    deep?: boolean,
    overwrite?: boolean
  ) => void
  /** Present on a real i18next instance; used to defer until init completes. */
  on?: (event: string, handler: () => void) => void
  isInitialized?: boolean
}

export const STALENESS_TRANSLATIONS = {
  en: {
    simulationV3: {
      staleness: {
        ariaLabel: 'Edition data is stale — {{count}} pending change',
        ariaLabel_plural: 'Edition data is stale — {{count}} pending changes',
        pendingChanges: '{{count}} pending change',
        pendingChanges_plural: '{{count}} pending changes',
        hint: '· click for details',
        // QA 2026-09-24 (Hebrew) — the edition screen's ribbon lead-in. It used
        // to be a `content:` string in `staleness.css`, which cannot translate:
        // the Hebrew screen read an English sentence beside a Hebrew count. The
        // CSS rule carried a FOLLOW-UP note asking for exactly this key.
        editionRibbonLeadIn: 'The edition data has changed since your last refresh —',
        // -----------------------------------------------------------
        // Story 4.15 (QA bugs 1 and 3) — the two sentences for a stale
        // edition with NOTHING to enumerate. Both used to render as the
        // literal "0 pending changes", which reads as "nothing to do"
        // beside a live [Refresh now] button.
        //
        // Neither takes the `_plural` family: there is no number in
        // either sentence. `count` is still passed by the chip (one call
        // site, all three kinds) and is simply not interpolated here.
        // -----------------------------------------------------------
        // The edition moved on while this arrangement stayed pinned — the
        // staleness rule's second clause. "Recalculated" is the planner's
        // own word for what an APPLY does, and matches the confirm
        // modal's "A new calc revision is recorded".
        recalculatedChip: 'Edition recalculated',
        recalculatedAria: 'Edition data is stale — the edition was recalculated since this arrangement was built',
        recalculatedNote:
        'The edition was recalculated since this arrangement was built. No catalogue changes to list — refreshing brings this arrangement onto the current revision.',
        // Reachable only through a corrupt stored fingerprint, where every
        // dimension is conservatively reported as moved while the counts,
        // read from the stored rows, can all be zero. Says exactly that
        // much and no more: promising a list we cannot produce would be
        // the same defect again.
        unspecifiedChip: 'Changes detected',
        unspecifiedAria: 'Edition data is stale — changes were detected that could not be itemised',
        unspecifiedNote:
        'Changes were detected, but they could not be itemised. Refreshing recomputes this edition from current data.',
        dismissLabel: 'Dismiss staleness alert',
        // -----------------------------------------------------------
        // Story 3.3 (FR-20, FR-21, UX-DR3/4/12) — popover, refresh
        // confirm modal, and the post-refresh transient. The per-
        // dimension count lines deliberately skip the `_plural` CLDR
        // treatment used above — "Archived: 1"/"Archived: 3" both read
        // fine as a flat label+number pair, so there's no second form
        // to translate (keeps the en/he key sets 1:1 without the base-
        // key stripping the RTL parity test already does for the chip).
        // -----------------------------------------------------------
        // QA 2026-09-30 — phrased as the client's SOW mockup writes them
        // ("1 product archived", "1 product moved sub-category"), not as
        // "Label: n". "Blocked" stays the planner's own word for the
        // Blocking-POS flag, as the product context menu says it.
        counts: {
        archived: '{{count}} product archived',
        archived_plural: '{{count}} products archived',
        unarchived: '{{count}} product unarchived',
        unarchived_plural: '{{count}} products unarchived',
        blocked: '{{count}} product blocked',
        blocked_plural: '{{count}} products blocked',
        unblocked: '{{count}} product unblocked',
        unblocked_plural: '{{count}} products unblocked',
        enteredScope: '{{count}} product entered scope',
        enteredScope_plural: '{{count}} products entered scope',
        leftScope: '{{count}} product left scope',
        leftScope_plural: '{{count}} products left scope',
        subCategoryMoves: '{{count}} product moved sub-category',
        subCategoryMoves_plural: '{{count}} products moved sub-category',
        brandSupplierMoves: '{{count}} product moved brand/supplier',
        brandSupplierMoves_plural: '{{count}} products moved brand/supplier',
        // Story 4.10 — a sub-category moved to a different category under the
        // department.
        departmentStructureChanges: '{{count}} department structure change',
        departmentStructureChanges_plural: '{{count}} department structure changes',
        storeSetChanged: '{{count}} store-set change',
        storeSetChanged_plural: '{{count}} store-set changes',
        },
        // QA 2026-09-30: the client's SOW wording, in place of the longer
        // explanation of why an archive+unarchive pair leaves no line.
        netOffsetNote: 'Note: changes that cancel out are not shown',
        // QA 2026-09-30 — the SOW's subtitle, naming the revision the last
        // calculation ran at. `recencyNoVersion` covers a response that carries
        // no revision id, so the line never reads "(vundefined, ...)".
        recency: 'Since last calculated (v{{version}}, {{count}} day ago)',
        recency_plural: 'Since last calculated (v{{version}}, {{count}} days ago)',
        recencyNoVersion: 'Since last calculated ({{count}} day ago)',
        recencyNoVersion_plural: 'Since last calculated ({{count}} days ago)',
        manualShareFlag: 'This edition includes a manually pinned share.',
        history: 'History',
        refreshNow: 'Refresh now',
        cancel: 'Cancel',
        refreshConfirmTitle: 'Refresh edition data',
        refreshConfirmDesc:
        'Refreshing applies the updated values to all saved planograms derived from it. A new calc revision is recorded; history is preserved in the log. Products that no longer qualify may be removed from the arrangement.',
        refreshFailed: 'Nothing was updated; your data is unchanged.',
        // Story 4.5 (AC 6): the stamp SUCCEEDED and only the screen failed to
        // catch up — so the containment line above, which promises nothing was
        // applied, would be false here.
        refreshReloadFailed:
        'The edition was refreshed, but the screen could not reload. Reload the page to see the new values.',
        // Story 4.5 (AC 4, decision D-a / VF-3) was a named list of the
        // products that no longer qualify. Withdrawn 2026-09-23 (QA + Ashan):
        // the list was computed against the WHOLE saved layout, so it repeated
        // every product archived in any earlier session on every refresh, and it
        // said "were removed from the arrangement" when decision D-a keeps the
        // arrangement untouched — they leave only on [Rearrange to sync]. The
        // refresh now confirms itself and nothing more; the products that no
        // longer qualify are listed where there is room for them (Issues ->
        // Filtered Out), marked on the canvas, and the banner asks for the
        // re-arrange.
        refreshComplete: 'Refresh completed successfully',
        // QA 2026-09-24 — the refresh reloads the session, so unsaved work (an
        // AI arrangement above all) is gone the moment it lands. Same question
        // the export and the data-panel download already ask (FR-68), with the
        // discard spelled out rather than implied.
        unsavedTitle: 'Unsaved changes',
        unsavedPrompt:
        'This simulation has changes you have not saved. Refreshing reloads it from the edition, so those changes will be lost.',
        saveAndRefresh: 'Save & refresh',
        justRefreshed: '(just refreshed)',
        // Story 3.4 (FR-19, UX-DR4/6) — dismiss-warning modal + top-bar
        // dot. Wording matches the T-07 UX prototype verbatim (the
        // reference behavior/strings source cited in the story).
        dismissTitle: 'Dismissing is not recommended',
        dismissBody:
        "You can clear the alert to keep working, but the data stays out of date — better to run a recalculation. A small indicator stays next to the version number so you won't forget.",
        dismissAnyway: 'Dismiss anyway',
        dotTooltip: 'Edition is stale — click to show changes',
        // Story 3.6 (FR-22, UX-DR7) — verbatim T-07 prototype string.
        arrangeStaleLine: 'Edition is stale',
        // 2026-09-14 UI correction: the ribbon lead used to read
        // "Edition is stale." — factually wrong here. The edition is at the
        // current revision (v{{currentRevision}}); it's the arrangement that's
        // behind at v{{arrangementRevision}}. "Out of sync" also mirrors the
        // action button copy on the same row ("Rearrange to sync").
        // QA 2026-09-30: the SOW reads "Arrangement built against v4 · edition
        // now at v6 · values are current" — one sentence, no "out of sync"
        // verdict in front of it. The subject stays bold; the body carries on
        // from it.
        arrangementBehindLead: 'Arrangement',
        // Story 3.7 (FR-25, D2) — the arrangement-stamp ribbon body. The lead
        // above names the subject, so this line no longer repeats "Arrangement".
        arrangementStampVersioned:
        'built against v{{arrangementRevision}} · edition now at v{{currentRevision}} · values are current',
        arrangementStampLegacy:
        'The arrangement predates the latest computation',
        rearrangeToSync: 'Rearrange to sync',
        // QA 2026-09-24 (Hebrew): this string never had a key. The ribbon called
        // `t('...viewHistory', 'View history')`, and an inline default is
        // returned for EVERY language - so it shipped as English in Hebrew and
        // looked translated in review.
        viewHistory: 'View history',
        // Story 3.5 (FR-20-log, UX-DR5, Acceptance gate 6) — the refresh
        // log drawer. Revision identifiers are permitted here (NFR-4's
        // second allowed surface, alongside the arrangement-stamp ribbon).
        historyTitle: 'Refresh log',
        historyColRevision: 'Revision',
        historyColBy: 'By',
        historyColWhen: 'When',
        historyCurrent: 'Current',
        historyEmpty: 'No refreshes recorded yet.',
        historyLoadFailed: 'Could not load the refresh log. Please try again.',
        // Code review patch (2026-08-11) — distinct from historyLoadFailed:
        // a not-found edition can't be fixed by retrying.
        historyNotFound: 'This edition could not be found.',
      },
    },
  },
  he: {
    simulationV3: {
      staleness: {
        // Hebrew plural buckets per i18next's OWN runtime resolution
        // (verified, see the `en.staleness` comment above): _0=one(1),
        // _1=two(2), _2=many(round tens ≥20), _3=other(0,3-19,21,...).
        // "שינוי"/"שינויים" only distinguish singular vs. plural in
        // Hebrew — the same plural noun form covers two/many/other.
        ariaLabel_0: 'נתוני המהדורה אינם מעודכנים — {{count}} שינוי ממתין',
        ariaLabel_1: 'נתוני המהדורה אינם מעודכנים — {{count}} שינויים ממתינים',
        ariaLabel_2: 'נתוני המהדורה אינם מעודכנים — {{count}} שינויים ממתינים',
        ariaLabel_3: 'נתוני המהדורה אינם מעודכנים — {{count}} שינויים ממתינים',
        pendingChanges_0: '{{count}} שינוי ממתין',
        pendingChanges_1: '{{count}} שינויים ממתינים',
        pendingChanges_2: '{{count}} שינויים ממתינים',
        pendingChanges_3: '{{count}} שינויים ממתינים',
        hint: '· לחצו לפרטים',
        editionRibbonLeadIn: 'נתוני המהדורה השתנו מאז הרענון האחרון —',
        // Story 4.15 — no plural family in either language: no number.
        recalculatedChip: 'המהדורה חושבה מחדש',
        recalculatedAria: 'נתוני המהדורה אינם מעודכנים — המהדורה חושבה מחדש מאז שנבנה הסידור',
        recalculatedNote:
        'המהדורה חושבה מחדש מאז שנבנה הסידור. אין שינויי קטלוג להציג — רענון יעדכן את הסידור לגרסת החישוב הנוכחית.',
        unspecifiedChip: 'זוהו שינויים',
        unspecifiedAria: 'נתוני המהדורה אינם מעודכנים — זוהו שינויים שלא ניתן לפרט',
        unspecifiedNote:
        'זוהו שינויים, אך לא ניתן לפרט אותם. רענון יחשב את המהדורה מחדש מהנתונים העדכניים.',
        dismissLabel: 'סגור התראה על נתונים לא מעודכנים',
        counts: {
        archived: '{{count}} מוצר הועבר לארכיון',
        archived_plural: '{{count}} מוצרים הועברו לארכיון',
        unarchived: '{{count}} מוצר שוחזר מארכיון',
        unarchived_plural: '{{count}} מוצרים שוחזרו מארכיון',
        blocked: '{{count}} מוצר נחסם',
        blocked_plural: '{{count}} מוצרים נחסמו',
        unblocked: '{{count}} מוצר שוחרר מחסימה',
        unblocked_plural: '{{count}} מוצרים שוחררו מחסימה',
        enteredScope: '{{count}} מוצר נכנס לטווח',
        enteredScope_plural: '{{count}} מוצרים נכנסו לטווח',
        leftScope: '{{count}} מוצר יצא מהטווח',
        leftScope_plural: '{{count}} מוצרים יצאו מהטווח',
        subCategoryMoves: '{{count}} מוצר עבר תת-קטגוריה',
        subCategoryMoves_plural: '{{count}} מוצרים עברו תת-קטגוריה',
        brandSupplierMoves: '{{count}} מוצר עבר מותג/ספק',
        brandSupplierMoves_plural: '{{count}} מוצרים עברו מותג/ספק',
        departmentStructureChanges: '{{count}} שינוי במבנה המחלקה',
        departmentStructureChanges_plural: '{{count}} שינויים במבנה המחלקה',
        storeSetChanged: '{{count}} שינוי במערך הסניפים',
        storeSetChanged_plural: '{{count}} שינויים במערך הסניפים',
        },
        netOffsetNote: 'הערה: שינויים שמתקזזים אינם מוצגים',
        recency_0: 'מאז החישוב האחרון (v{{version}}, לפני {{count}} יום)',
        recency_1: 'מאז החישוב האחרון (v{{version}}, לפני {{count}} ימים)',
        recency_2: 'מאז החישוב האחרון (v{{version}}, לפני {{count}} ימים)',
        recency_3: 'מאז החישוב האחרון (v{{version}}, לפני {{count}} ימים)',
        recencyNoVersion_0: 'מאז החישוב האחרון (לפני {{count}} יום)',
        recencyNoVersion_1: 'מאז החישוב האחרון (לפני {{count}} ימים)',
        recencyNoVersion_2: 'מאז החישוב האחרון (לפני {{count}} ימים)',
        recencyNoVersion_3: 'מאז החישוב האחרון (לפני {{count}} ימים)',
        manualShareFlag: 'מהדורה זו כוללת נתח שנקבע ידנית.',
        history: 'היסטוריה',
        refreshNow: 'רענן עכשיו',
        cancel: 'ביטול',
        refreshConfirmTitle: 'רענון נתוני המהדורה',
        refreshConfirmDesc:
        'הרענון מחיל את הערכים המעודכנים על כל הפלנוגרמות השמורות שנגזרות ממהדורה זו. נרשמת גרסת חישוב חדשה; ההיסטוריה נשמרת ביומן. מוצרים שאינם עומדים עוד בתנאים עשויים להיות מוסרים מהסידור.',
        refreshFailed: 'שום דבר לא עודכן; הנתונים שלך לא השתנו.',
        refreshReloadFailed:
        'המהדורה רועננה, אך המסך לא נטען מחדש. רענן את הדף כדי לראות את הערכים החדשים.',
        refreshComplete: 'הרענון הושלם בהצלחה',
        unsavedTitle: 'שינויים שלא נשמרו',
        unsavedPrompt:
        'לסימולציה הזו יש שינויים שלא נשמרו. הרענון טוען אותה מחדש מהמהדורה, ולכן השינויים האלה יאבדו.',
        saveAndRefresh: 'שמירה ורענון',
        justRefreshed: '(רוענן זה עתה)',
        dismissTitle: 'התעלמות לא מומלצת',
        dismissBody:
        'אפשר לפנות את ההתראה כדי לעבוד, אבל הנתונים יישארו לא מעודכנים — עדיף להריץ חישוב מחדש. אינדיקציה קטנה תישאר ליד מספר הגרסה כדי שלא תשכח.',
        dismissAnyway: 'התעלם בכל זאת',
        dotTooltip: 'נתוני המהדורה אינם מעודכנים — לחצו להצגת השינויים',
        arrangeStaleLine: 'מהדורה עבשה',
        arrangementBehindLead: 'הסידור',
        arrangementStampVersioned:
        'נבנה על גרסה v{{arrangementRevision}} · המהדורה כעת בגרסה v{{currentRevision}} · הערכים מעודכנים',
        arrangementStampLegacy: 'הסידור קדם לחישוב האחרון',
        rearrangeToSync: 'סדר מחדש לסנכרון',
        viewHistory: 'הצג היסטוריה',
        historyTitle: 'לוג ריענונים',
        historyColRevision: 'גרסה',
        historyColBy: 'בוצע ע״י',
        historyColWhen: 'מתי',
        historyCurrent: 'נוכחי',
        historyEmpty: 'טרם נרשמו ריענונים.',
        historyLoadFailed: 'טעינת לוג הריענונים נכשלה. אנא נסה שוב.',
        historyNotFound: 'לא ניתן היה למצוא מהדורה זו.',
      },
    },
  },
}

/**
 * Registers the staleness strings onto an i18next instance. Idempotent
 * (`deep`/`overwrite` both true) and defensive about a stub host, so BOTH entry
 * points — the v3 screen via `registerSimulationV3Translations`, and the legacy
 * edition screen directly — may call it unconditionally at module scope.
 */
const applyBundles = (target: ResourceHost): void => {
  Object.entries(STALENESS_TRANSLATIONS).forEach(([language, bundle]) => {
    target.addResourceBundle?.(language, 'translation', bundle, true, true)
  })
}

export const registerStalenessTranslations = (
  target: ResourceHost = i18n as ResourceHost
): void => {
  if (!target) return
  if (typeof target.addResourceBundle === 'function') {
    applyBundles(target)
    return
  }
  // Code review 2026-09-14: i18next 19 assigns `addResourceBundle` onto the
  // instance INSIDE `init()` (i18next.js:1937), not on the prototype. So a
  // module-scope caller that happens to evaluate before the app's own
  // `_translations/i18n.js` init would hit the old guard and return SILENTLY —
  // turning a load-order problem into raw key strings on screen, which is the
  // worse failure mode. The v3 screen never had this exposure because it loads
  // lazily, long after init; the legacy call site does.
  //
  // Defer instead of giving up: register the moment the instance is ready.
  if (typeof target.on === 'function') {
    target.on('initialized', () => applyBundles(target))
  }
}
